// src/components/dependants/sharedNoteEditor.component.jsx
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Extension } from "@tiptap/core";
import { EditorContent, useEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Collaboration from "@tiptap/extension-collaboration";
import TextStyle from "@tiptap/extension-text-style";
import Color from "@tiptap/extension-color";
import Highlight from "@tiptap/extension-highlight";
import Underline from "@tiptap/extension-underline";
import Link from "@tiptap/extension-link";
import TextAlign from "@tiptap/extension-text-align";
import { Plugin, PluginKey } from "prosemirror-state";
import { Decoration, DecorationSet } from "prosemirror-view";
import * as Y from "yjs";
import { getUserFromToken } from "../../../utils/functions.ts";

const EMPTY_DOC = {
  type: "doc",
  content: [{ type: "paragraph" }],
};
const AUTOSAVE_DELAY_MS = 1000;
const SHARED_NOTE_FIELD = "shared-note";
const YJS_NOTE_KIND = "yjs-v1";

const isValidDoc = (value) => value && typeof value === "object" && value.type === "doc";

const parseJsonMaybe = (value) => {
  if (isValidDoc(value)) return value;
  if (typeof value !== "string" || value.trim() === "") return null;
  try {
    const parsed = JSON.parse(value);
    return isValidDoc(parsed) ? parsed : null;
  } catch {
    return null;
  }
};

const getRawNoteJson = (value) => {
  if (!value || typeof value !== "object") return "";
  return value.noteJson || value.NoteJson || value.noteJSON || "";
};

const parseStoredNoteJson = (value) => {
  const raw = getRawNoteJson(value);
  if (!raw) return null;
  if (typeof raw === "object") return raw;
  if (typeof raw !== "string" || raw.trim() === "") return null;
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
};

const parseStoredDocValue = (value) => {
  if (isValidDoc(value)) return value;
  if (typeof value !== "string" || value.trim() === "") return null;
  try {
    const parsed = JSON.parse(value);
    if (isValidDoc(parsed)) return parsed;
    if (parsed?.kind === YJS_NOTE_KIND) return parseJsonMaybe(parsed.doc);
    return null;
  } catch {
    return null;
  }
};

const getYjsState = (value) => {
  const stored = parseStoredNoteJson(value);
  return stored?.kind === YJS_NOTE_KIND && typeof stored.state === "string" ? stored.state : "";
};

const getNoteJson = (value) => {
  if (!value || typeof value !== "object") return null;
  const stored = parseStoredNoteJson(value);
  if (stored?.kind === YJS_NOTE_KIND) return parseJsonMaybe(stored.doc);
  return parseJsonMaybe(getRawNoteJson(value));
};

const getLastSeenDoc = (value) => parseStoredDocValue(value?.lastSeenDoc || value?.LastSeenDoc || "");

const getNormalizedTextBlocks = (doc) => {
  const blocks = [];
  const walk = (node) => {
    if (!node || typeof node !== "object") return "";
    if (node.type === "text") return String(node.text || "");
    const childText = Array.isArray(node.content) ? node.content.map(walk).join("") : "";
    if (["paragraph", "heading", "blockquote", "listItem"].includes(node.type)) {
      const clean = childText.replace(/\s+/g, " ").trim();
      if (clean) blocks.push(clean);
    }
    return childText;
  };
  walk(doc);
  return blocks;
};

const hasUnreadChanges = (value) => value?.hasUnreadChanges === true || value?.hasUnreadChanges === 1 || value?.hasUnreadChanges === "1";

const TemporaryChangeHighlight = Extension.create({
  name: "temporaryChangeHighlight",

  addOptions() {
    return {
      previousDoc: null,
      enabled: false,
    };
  },

  addProseMirrorPlugins() {
    const previousBlocks = new Set(getNormalizedTextBlocks(this.options.previousDoc));
    const enabled = this.options.enabled === true && previousBlocks.size > 0;

    const buildDecorations = (doc) => {
      if (!enabled) return DecorationSet.empty;

      const decorations = [];
      doc.descendants((node, pos) => {
        if (!node.isTextblock) return true;
        const cleanText = node.textContent.replace(/\s+/g, " ").trim();
        if (!cleanText || previousBlocks.has(cleanText)) return true;

        decorations.push(Decoration.inline(pos + 1, pos + node.nodeSize - 1, {
          style: "background-color: #fff176; box-shadow: inset 0 -2px 0 #f6d32d;",
          class: "shared-note-temporary-change",
        }));
        return false;
      });

      return DecorationSet.create(doc, decorations);
    };

    return [
      new Plugin({
        key: new PluginKey("temporaryChangeHighlight"),
        state: {
          init: (_, state) => buildDecorations(state.doc),
          apply: (transaction, oldDecorations, oldState, newState) => {
            if (!transaction.docChanged && oldState.doc === newState.doc) return oldDecorations;
            return buildDecorations(newState.doc);
          },
        },
        props: {
          decorations(state) {
            return this.getState(state);
          },
        },
      }),
    ];
  },
});

const getLegacyNoteHtml = (value) => {
  const raw = getRawNoteJson(value);
  if (typeof raw !== "string" || raw.trim() === "") return "";
  return parseStoredNoteJson(value) ? "" : raw;
};

const getInitialEditorContent = (value) => getNoteJson(value) || getLegacyNoteHtml(value) || EMPTY_DOC;

const stringifyDoc = (doc) => JSON.stringify(doc || EMPTY_DOC);

const encodeBase64 = (bytes) => {
  let binary = "";
  const chunkSize = 0x8000;
  for (let index = 0; index < bytes.length; index += chunkSize) {
    binary += String.fromCharCode(...bytes.subarray(index, index + chunkSize));
  }
  return window.btoa(binary);
};

const decodeBase64 = (value) => {
  const binary = window.atob(value);
  const bytes = new Uint8Array(binary.length);
  for (let index = 0; index < binary.length; index += 1) {
    bytes[index] = binary.charCodeAt(index);
  }
  return bytes;
};

const applyYjsState = (doc, state) => {
  if (!doc || !state) return false;
  try {
    Y.applyUpdate(doc, decodeBase64(state));
    return true;
  } catch (err) {
    console.error("Unable to apply shared note Yjs state:", err);
    return false;
  }
};

const getYjsPayload = (doc, snapshot) => ({
  kind: YJS_NOTE_KIND,
  state: encodeBase64(Y.encodeStateAsUpdate(doc)),
  doc: snapshot || EMPTY_DOC,
});

export default function SharedNoteEditor({
  note,
  onDirtyChange,
  onRefreshRequiredChange,
  onSaved,
  onSaveError,
  readOnly = false,
  placeholder = "Enter Note",
}) {
  const notesEndpoint = "https://www.fbxchange.com/ReactPHP/notes.php";

  const initialContent = useMemo(() => getInitialEditorContent(note), [note]);
  const noteKey = note?.id || note?.ID || note?.noteId || "shared-note";
  const initialYjsState = useMemo(() => getYjsState(note), [note]);
  const lastSeenDoc = useMemo(() => getLastSeenDoc(note), [note]);
  const showTemporaryHighlights = hasUnreadChanges(note);
  const yDoc = useMemo(() => {
    const nextDoc = new Y.Doc();
    applyYjsState(nextDoc, initialYjsState);
    return nextDoc;
    // Keep the Yjs document stable while the same shared note row is open.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [noteKey]);
  const [dirty, setDirty] = useState(false);
  const [pendingRemoteNote, setPendingRemoteNote] = useState(null);
  const [remoteNotice, setRemoteNotice] = useState("");
  const [, setSaveState] = useState("idle");
  const [autosaveRevision, setAutosaveRevision] = useState(0);
  const dirtyRef = useRef(false);
  const hydratedRef = useRef(false);
  const latestNoteRef = useRef(note);
  const suppressProgrammaticChangeRef = useRef(false);
  const originalJsonRef = useRef(null);
  const autosaveTimerRef = useRef(null);
  const savingRef = useRef(false);
  const overwriteConflictRef = useRef(false);
  const seededYjsRef = useRef(false);
  const toolbarSelectionRef = useRef(null);
  const scrollWrapRef = useRef(null);
  const pendingScrollRestoreRef = useRef(null);
  const editorRef = useRef(null);

  useEffect(() => {
    seededYjsRef.current = false;
  }, [noteKey]);

  const getNoteVersion = useCallback((value) => [
    value?.id || "",
    value?.lastUpdated || value?.updatedAt || "",
    value?.lastEditBy || "",
    getYjsState(value),
    stringifyDoc(getNoteJson(value)),
    getLegacyNoteHtml(value),
  ].join("|"), []);

  const getEditorName = useCallback((value) => value?.lastEditBy || value?.updatedBy || "someone else", []);
  const getEditedAt = useCallback((value) => value?.lastUpdated || value?.updatedAt || "", []);

  const getEditorScrollElement = useCallback(() => (
    scrollWrapRef.current?.querySelector(".shared-note-tiptap-editor") || null
  ), []);

  const rememberScrollPosition = useCallback(() => {
    const activeEditor = editorRef.current;
    const editorDom = activeEditor?.view?.dom || null;
    const activeElement = document.activeElement;
    const editorFocused = !!editorDom && (
      activeElement === editorDom || editorDom.contains(activeElement)
    );
    const selection = activeEditor?.state?.selection;

    pendingScrollRestoreRef.current = {
      wrapTop: scrollWrapRef.current?.scrollTop || 0,
      editorTop: getEditorScrollElement()?.scrollTop || 0,
      windowX: window.scrollX || 0,
      windowY: window.scrollY || 0,
      selectionFrom: selection?.from,
      selectionTo: selection?.to,
      editorJson: activeEditor ? stringifyDoc(activeEditor.getJSON()) : "",
      editorFocused,
      token: Date.now(),
    };
  }, [getEditorScrollElement]);

  const restoreRememberedScrollPosition = useCallback(() => {
    const snapshot = pendingScrollRestoreRef.current;
    if (!snapshot) return;

    const applyScrollSnapshot = () => {
      if (scrollWrapRef.current) scrollWrapRef.current.scrollTop = snapshot.wrapTop;
      const editorScrollElement = getEditorScrollElement();
      if (editorScrollElement) editorScrollElement.scrollTop = snapshot.editorTop;
      window.scrollTo(snapshot.windowX, snapshot.windowY);
    };

    const applyEditorSelectionSnapshot = () => {
      const activeEditor = editorRef.current;
      const selectionFrom = Number(snapshot.selectionFrom);
      const selectionTo = Number(snapshot.selectionTo);
      if (!activeEditor || !Number.isFinite(selectionFrom) || !Number.isFinite(selectionTo)) return;
      if (snapshot.editorJson && stringifyDoc(activeEditor.getJSON()) !== snapshot.editorJson) return;

      const maxPosition = activeEditor.state.doc.content.size;
      const from = Math.max(0, Math.min(selectionFrom, maxPosition));
      const to = Math.max(0, Math.min(selectionTo, maxPosition));

      try {
        activeEditor.commands.setTextSelection({ from, to });
        if (snapshot.editorFocused) {
          activeEditor.commands.focus();
        }
      } catch {
        // The document may have changed shape during a remote merge; leave the editor's current selection alone.
      }
    };

    window.requestAnimationFrame(() => {
      applyScrollSnapshot();
      applyEditorSelectionSnapshot();
      window.requestAnimationFrame(() => {
        applyScrollSnapshot();
        applyEditorSelectionSnapshot();
      });
    });

    [50, 150, 350, 700].forEach((delay) => {
      window.setTimeout(() => {
        if (pendingScrollRestoreRef.current?.token !== snapshot.token) return;
        applyScrollSnapshot();
        applyEditorSelectionSnapshot();
        if (delay === 700) {
          pendingScrollRestoreRef.current = null;
        }
      }, delay);
    });
  }, [getEditorScrollElement]);

  const setDirtyState = useCallback((nextDirty) => {
    dirtyRef.current = nextDirty;
    setDirty(nextDirty);
    if (typeof onDirtyChange === "function") onDirtyChange(nextDirty);
  }, [onDirtyChange]);

  const extensions = useMemo(() => [
    StarterKit.configure({
      history: false,
      heading: { levels: [1, 2, 3] },
    }),
    Collaboration.configure({
      document: yDoc,
      field: SHARED_NOTE_FIELD,
    }),
    TextStyle,
    Color,
    Highlight.configure({ multicolor: true }),
    Underline,
    Link.configure({
      openOnClick: false,
      autolink: true,
      linkOnPaste: true,
    }),
    TextAlign.configure({
      types: ["heading", "paragraph"],
    }),
    TemporaryChangeHighlight.configure({
      previousDoc: lastSeenDoc,
      enabled: showTemporaryHighlights,
    }),
  ], [lastSeenDoc, showTemporaryHighlights, yDoc]);

  const editor = useEditor({
    extensions,
    editable: !readOnly,
    editorProps: {
      attributes: {
        class: "shared-note-tiptap-editor",
        "data-placeholder": placeholder,
      },
    },
    onCreate: ({ editor: createdEditor }) => {
      const fragment = yDoc.getXmlFragment(SHARED_NOTE_FIELD);
      if (!initialYjsState && !seededYjsRef.current && fragment.length === 0) {
        seededYjsRef.current = true;
        suppressProgrammaticChangeRef.current = true;
        createdEditor.commands.setContent(initialContent, false);
      }
      originalJsonRef.current = stringifyDoc(createdEditor.getJSON());
    },
    onUpdate: ({ editor: updatedEditor }) => {
      if (suppressProgrammaticChangeRef.current) {
        suppressProgrammaticChangeRef.current = false;
        originalJsonRef.current = stringifyDoc(updatedEditor.getJSON());
        setDirtyState(false);
        return;
      }

      setSaveState("idle");
      setRemoteNotice("");
      const nextDirty = stringifyDoc(updatedEditor.getJSON()) !== originalJsonRef.current;
      setDirtyState(nextDirty);
      if (nextDirty) {
        setAutosaveRevision((current) => current + 1);
      }
    },
  }, [noteKey]);

  useEffect(() => {
    editorRef.current = editor;
  }, [editor]);

  const applyIncomingNote = useCallback((incomingNote, options = {}) => {
    latestNoteRef.current = incomingNote;
    setPendingRemoteNote(null);
    if (!editor) return;

    rememberScrollPosition();
    suppressProgrammaticChangeRef.current = true;
    editor.commands.setContent(getInitialEditorContent(incomingNote), false);
    originalJsonRef.current = stringifyDoc(editor.getJSON());
    setDirtyState(false);
    restoreRememberedScrollPosition();

    if (options.showNotice) {
      const when = getEditedAt(incomingNote);
      setRemoteNotice(`Updated by ${getEditorName(incomingNote)}${when ? ` at ${when}` : ""}.`);
    }
  }, [editor, getEditedAt, getEditorName, rememberScrollPosition, restoreRememberedScrollPosition, setDirtyState]);

  const applyRemoteYjsNote = useCallback((incomingNote, options = {}) => {
    const incomingState = getYjsState(incomingNote);
    if (!incomingState) return false;

    const wasDirty = dirtyRef.current;
    latestNoteRef.current = incomingNote;
    setPendingRemoteNote(null);
    rememberScrollPosition();
    applyYjsState(yDoc, incomingState);

    const nextJson = editor ? stringifyDoc(editor.getJSON()) : originalJsonRef.current;
    if (options.keepDirty || wasDirty) {
      setDirtyState(true);
      setAutosaveRevision((current) => current + 1);
    } else {
      originalJsonRef.current = nextJson;
      setDirtyState(false);
    }

    if (options.showNotice) {
      const when = getEditedAt(incomingNote);
      setRemoteNotice(`Merged update from ${getEditorName(incomingNote)}${when ? ` at ${when}` : ""}.`);
    }
    restoreRememberedScrollPosition();
    return true;
  }, [editor, getEditedAt, getEditorName, rememberScrollPosition, restoreRememberedScrollPosition, setDirtyState, yDoc]);

  useEffect(() => {
    hydratedRef.current = true;
  }, []);

  useEffect(() => {
    if (!editor) return;

    const incomingVersion = getNoteVersion(note);
    const currentVersion = getNoteVersion(latestNoteRef.current);
    if (incomingVersion === currentVersion) return;

    if (getYjsState(note)) {
      const incomingDoc = getNoteJson(note);
      const currentDoc = editor ? editor.getJSON() : null;
      if (incomingDoc && currentDoc && stringifyDoc(incomingDoc) === stringifyDoc(currentDoc)) {
        latestNoteRef.current = note;
        originalJsonRef.current = stringifyDoc(currentDoc);
        setDirtyState(false);
        hydratedRef.current = true;
        return;
      }

      applyRemoteYjsNote(note, {
        showNotice: hydratedRef.current,
        keepDirty: dirtyRef.current,
      });
      hydratedRef.current = true;
      return;
    }

    if (dirtyRef.current) {
      setPendingRemoteNote(note);
      latestNoteRef.current = note;
      return;
    }

    const shouldShowNotice = hydratedRef.current;
    applyIncomingNote(note, { showNotice: shouldShowNotice });
    hydratedRef.current = true;
  }, [applyIncomingNote, applyRemoteYjsNote, editor, getNoteVersion, note]);

  const letsSaveTheNote = useCallback(async (options = {}) => {
    const user = await getUserFromToken();
    if (!editor) return;
    if (savingRef.current) return;

    try {
      if (typeof onSaveError === "function") onSaveError("");
      rememberScrollPosition();
      setSaveState("saving");
      savingRef.current = true;
      const sourceNote = latestNoteRef.current && typeof latestNoteRef.current === "object" ? latestNoteRef.current : note;
      const snapshotJson = editor.getJSON();
      const noteJson = getYjsPayload(yDoc, snapshotJson);
      const requestedJson = stringifyDoc(snapshotJson);
      const nextNote = {
        ...sourceNote,
        id: sourceNote?.id ?? sourceNote?.ID ?? sourceNote?.noteId,
        team: sourceNote?.team ?? sourceNote?.Team ?? sourceNote?.VisibleTo,
        noteJson,
        plainText: editor.getText("\n"),
        expectedUpdatedAt: sourceNote?.lastUpdated || sourceNote?.updatedAt || "",
      };
      const payload = {
        note: nextNote,
        original: sourceNote,
        scriptName: "saveSharedTeamNote",
        user,
        forceSave: options.force === true || overwriteConflictRef.current === true,
      };
      overwriteConflictRef.current = false;

      const res = await fetch(notesEndpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        const text = await res.text();
        console.error("Save failed:", res.status, text);
        setSaveState("error");
        if (typeof onSaveError === "function") onSaveError(`Unable to save shared note. Server returned ${res.status}.`);
        return;
      }

      const rawText = await res.text();
      let data = {};
      try {
        data = JSON.parse(rawText || "{}");
      } catch {
        console.error("Save returned non-JSON:", rawText);
        setSaveState("error");
        if (typeof onSaveError === "function") onSaveError("Unable to save shared note. Server returned an invalid response.");
        return;
      }

      if (data?.conflict === true) {
        const remoteNote = data?.note || data?.currentNote || null;
        if (remoteNote && getYjsState(remoteNote)) {
          applyRemoteYjsNote(remoteNote, { showNotice: true, keepDirty: true });
          setSaveState("idle");
          if (typeof onSaveError === "function") onSaveError("");
          return;
        } else if (remoteNote) {
          latestNoteRef.current = remoteNote;
          setPendingRemoteNote(remoteNote);
          setRemoteNotice(`Updated by ${getEditorName(remoteNote)}${getEditedAt(remoteNote) ? ` at ${getEditedAt(remoteNote)}` : ""}.`);
        }
        setSaveState("conflict");
        if (typeof onSaveError === "function") onSaveError("");
        return;
      }

      if (data?.success !== true) {
        setSaveState("error");
        if (typeof onSaveError === "function") onSaveError(data?.error || data?.message || "Unable to save shared note.");
        return;
      }

      const savedNote = data?.note || nextNote;
      latestNoteRef.current = savedNote;
      originalJsonRef.current = requestedJson;
      const currentJson = stringifyDoc(editor.getJSON());
      const stillDirty = currentJson !== requestedJson;
      setDirtyState(stillDirty);
      if (!stillDirty) {
        setPendingRemoteNote(null);
        setSaveState("saved");
        setRemoteNotice("");
      } else {
        setSaveState("idle");
      }
      if (typeof onSaved === "function") onSaved(savedNote);
      restoreRememberedScrollPosition();
    } catch (err) {
      console.error("Save error:", err);
      setSaveState("error");
      if (typeof onSaveError === "function") onSaveError("Unable to save shared note.");
    } finally {
      savingRef.current = false;
    }
  }, [applyRemoteYjsNote, editor, getEditedAt, getEditorName, note, onSaveError, onSaved, rememberScrollPosition, restoreRememberedScrollPosition, setDirtyState, yDoc]);

  useEffect(() => {
    restoreRememberedScrollPosition();
  }, [note, restoreRememberedScrollPosition]);

  useEffect(() => {
    if (!dirty || pendingRemoteNote || readOnly || !editor) return undefined;

    if (autosaveTimerRef.current) {
      window.clearTimeout(autosaveTimerRef.current);
    }
    autosaveTimerRef.current = window.setTimeout(() => {
      autosaveTimerRef.current = null;
      letsSaveTheNote();
    }, AUTOSAVE_DELAY_MS);

    return () => {
      if (autosaveTimerRef.current) {
        window.clearTimeout(autosaveTimerRef.current);
        autosaveTimerRef.current = null;
      }
    };
  }, [autosaveRevision, dirty, editor, letsSaveTheNote, pendingRemoteNote, readOnly]);

  useEffect(() => () => {
    if (autosaveTimerRef.current) {
      window.clearTimeout(autosaveTimerRef.current);
    }
  }, []);

  useEffect(() => {
    if (typeof onRefreshRequiredChange === "function") {
      onRefreshRequiredChange(Boolean(pendingRemoteNote));
    }
  }, [onRefreshRequiredChange, pendingRemoteNote]);

  const keepMyEdit = () => {
    setPendingRemoteNote(null);
    overwriteConflictRef.current = true;
    setRemoteNotice("You are keeping your edit. Autosave will replace the latest shared note.");
    window.setTimeout(() => letsSaveTheNote({ force: true }), 0);
  };

  const setLink = () => {
    if (!editor) return;
    const previousUrl = editor.getAttributes("link").href || "";
    const url = window.prompt("Link URL", previousUrl);
    if (url === null) return;
    if (url.trim() === "") {
      editor.chain().focus().unsetLink().run();
      return;
    }
    editor.chain().focus().setLink({ href: url.trim() }).run();
  };

  const rememberToolbarSelection = () => {
    if (!editor) return;
    const { from, to } = editor.state.selection;
    toolbarSelectionRef.current = { from, to };
  };

  const restoreToolbarSelection = (chain) => {
    const selection = toolbarSelectionRef.current;
    if (!selection) return chain.focus();

    const maxPosition = editor.state.doc.content.size;
    const from = Math.max(0, Math.min(selection.from, maxPosition));
    const to = Math.max(0, Math.min(selection.to, maxPosition));
    return chain.focus().setTextSelection({ from, to });
  };

  const applyTextColor = (color) => {
    if (!editor || readOnly || !color) return;
    restoreToolbarSelection(editor.chain()).setColor(color).run();
  };

  const applyHighlightColor = (color) => {
    if (!editor || readOnly || !color) return;
    restoreToolbarSelection(editor.chain()).toggleHighlight({ color }).run();
  };

  const toolbarButton = (label, action, active = false, disabled = false) => (
    <button
      type="button"
      className={`btn btn-sm ${active ? "btn-primary" : "btn-outline-secondary"}`}
      disabled={disabled || !editor || readOnly}
      onClick={action}
    >
      {label}
    </button>
  );

  return (
    <div className="note-editor-scroll w-100" ref={scrollWrapRef}>
      {(pendingRemoteNote || remoteNotice) && (
        <div className="d-flex align-items-center gap-2 mt-2 mb-2">
          {pendingRemoteNote ? (
            <div className="alert alert-warning py-1 px-2 mb-0 flex-grow-1">
            Updated by {getEditorName(pendingRemoteNote)}{getEditedAt(pendingRemoteNote) ? ` at ${getEditedAt(pendingRemoteNote)}` : ""}. Refresh before saving, or keep your edit and replace the latest note.
            <button
              type="button"
              className="btn btn-sm btn-warning ms-2"
              onClick={() => applyIncomingNote(pendingRemoteNote, { showNotice: true })}
            >
              Refresh
            </button>
            <button
              type="button"
              className="btn btn-sm btn-outline-dark ms-2"
              onClick={keepMyEdit}
            >
              Keep my edit
            </button>
            </div>
          ) : (
            <div className="alert alert-info py-1 px-2 mb-0 flex-grow-1">
            {remoteNotice}
            </div>
          )}
        </div>
      )}

      <div className="shared-note-tiptap-toolbar">
        {toolbarButton("Normal", () => editor.chain().focus().setParagraph().run(), editor?.isActive("paragraph"))}
        {toolbarButton("H1", () => editor.chain().focus().toggleHeading({ level: 1 }).run(), editor?.isActive("heading", { level: 1 }))}
        {toolbarButton("H2", () => editor.chain().focus().toggleHeading({ level: 2 }).run(), editor?.isActive("heading", { level: 2 }))}
        {toolbarButton("H3", () => editor.chain().focus().toggleHeading({ level: 3 }).run(), editor?.isActive("heading", { level: 3 }))}
        <label className="shared-note-color-control">
          Text
          <input
            type="color"
            disabled={!editor || readOnly}
            onMouseDown={rememberToolbarSelection}
            onFocus={rememberToolbarSelection}
            onInput={(event) => applyTextColor(event.currentTarget.value)}
            onChange={(event) => applyTextColor(event.currentTarget.value)}
          />
        </label>
        <label className="shared-note-color-control">
          Highlight
          <input
            type="color"
            disabled={!editor || readOnly}
            onMouseDown={rememberToolbarSelection}
            onFocus={rememberToolbarSelection}
            onInput={(event) => applyHighlightColor(event.currentTarget.value)}
            onChange={(event) => applyHighlightColor(event.currentTarget.value)}
          />
        </label>
        {toolbarButton("B", () => editor.chain().focus().toggleBold().run(), editor?.isActive("bold"))}
        {toolbarButton("I", () => editor.chain().focus().toggleItalic().run(), editor?.isActive("italic"))}
        {toolbarButton("U", () => editor.chain().focus().toggleUnderline().run(), editor?.isActive("underline"))}
        {toolbarButton("S", () => editor.chain().focus().toggleStrike().run(), editor?.isActive("strike"))}
        {toolbarButton("Bullets", () => editor.chain().focus().toggleBulletList().run(), editor?.isActive("bulletList"))}
        {toolbarButton("Numbers", () => editor.chain().focus().toggleOrderedList().run(), editor?.isActive("orderedList"))}
        {toolbarButton("Outdent", () => editor.chain().focus().liftListItem("listItem").run(), false, !editor?.can().liftListItem("listItem"))}
        {toolbarButton("Indent", () => editor.chain().focus().sinkListItem("listItem").run(), false, !editor?.can().sinkListItem("listItem"))}
        {toolbarButton("Left", () => editor.chain().focus().setTextAlign("left").run(), editor?.isActive({ textAlign: "left" }))}
        {toolbarButton("Center", () => editor.chain().focus().setTextAlign("center").run(), editor?.isActive({ textAlign: "center" }))}
        {toolbarButton("Right", () => editor.chain().focus().setTextAlign("right").run(), editor?.isActive({ textAlign: "right" }))}
        {toolbarButton("Link", setLink, editor?.isActive("link"))}
      </div>

      <div
        onBlur={(event) => {
          if (event.currentTarget.contains(event.relatedTarget)) return;
          if (dirtyRef.current && !pendingRemoteNote && !readOnly) {
            letsSaveTheNote();
          }
        }}
      >
        <EditorContent editor={editor} />
      </div>

      <style>{`
        .note-editor-scroll {
          flex: 1;
          overflow-y: auto;
          overflow-x: hidden;
          padding-top: 6px;
          scrollbar-width: none;
          -ms-overflow-style: none;
        }
        .note-editor-scroll::-webkit-scrollbar { width: 0; height: 0; display: none; }
        .shared-note-tiptap-toolbar {
          position: sticky;
          top: 0;
          z-index: 20;
          display: flex;
          flex-wrap: wrap;
          gap: 6px;
          align-items: center;
          padding: 8px;
          border: 1px solid #d5dbe6;
          border-bottom: 0;
          border-radius: 4px 4px 0 0;
          background: var(--bs-body-bg, #fff);
        }
        .shared-note-color-control {
          display: inline-flex;
          align-items: center;
          gap: 4px;
          height: 31px;
          padding: 0 8px;
          border: 1px solid #6c757d;
          border-radius: 4px;
          color: #495057;
          font-size: 12px;
          font-weight: 700;
        }
        .shared-note-color-control input {
          width: 22px;
          height: 22px;
          padding: 0;
          border: 0;
          background: transparent;
        }
        .shared-note-save-state {
          min-width: 92px;
          color: #6c757d;
          font-size: 12px;
          font-weight: 800;
          text-align: right;
        }
        .shared-note-save-state-saving {
          color: #0d6efd;
        }
        .shared-note-save-state-error,
        .shared-note-save-state-conflict {
          color: #b02a37;
        }
        .shared-note-tiptap-editor {
          min-height: 460px;
          max-height: 620px;
          overflow-y: auto;
          overflow-x: hidden;
          padding: 14px;
          border: 1px solid #d5dbe6;
          border-radius: 0 0 4px 4px;
          outline: none;
          overflow-wrap: anywhere;
          word-break: break-word;
        }
        .shared-note-tiptap-editor p {
          margin: 0 0 0.7rem;
        }
        .shared-note-tiptap-editor ul,
        .shared-note-tiptap-editor ol {
          padding-left: 1.35rem;
        }
        .shared-note-tiptap-editor a {
          color: #0d6efd;
          text-decoration: underline;
        }
      `}</style>
    </div>
  );
}
